import { useLocation } from "react-router-dom";

// Import all skeleton components
import { AuthLoadingSkeleton } from "./skeletons/AuthLoadingSkeleton";
import { AdminSkeleton } from "./skeletons/AdminSkeleton";
import { BreakTimerSkeleton } from "./skeletons/BreakTimerSkeleton";
import { BreakCompleteSkeleton } from "./skeletons/BreakCompleteSkeleton";
import { OnboardingSkeleton } from "./skeletons/OnboardingSkeleton";
import { GenericSkeleton } from "./skeletons/GenericSkeleton";
import { DashboardSkeleton } from "./skeletons/DashboardSkeleton";
import { TimerSkeleton } from "./skeletons/TimerSkeleton";
import { LeaderboardSkeleton } from "./skeletons/LeaderboardSkeleton";
import { ProfileSkeleton } from "./skeletons/ProfileSkeleton";
import { ShopSkeleton } from "./skeletons/ShopSkeleton";
import { GroupsSkeleton } from "./skeletons/GroupsSkeleton";

export interface UnifiedLoadingSkeletonProps {
  /** Override auto-detected route */
  route?: string;
  /** Context hint for special loading states */
  context?: 'auth' | 'suspense' | 'admin';
  /** Custom message for auth loading skeleton */
  message?: string;
}

/**
 * Safe hook to get pathname - falls back gracefully if router context unavailable
 */
function useSafePathname(): string | null {
  try {
    const location = useLocation();
    return location.pathname;
  } catch {
    // Router context not available (e.g., during auth transitions)
    return null;
  }
}

/**
 * Unified loading skeleton that intelligently renders page-specific skeletons
 * based on the current route or provided context.
 * 
 * @example
 * // Auto-detect route
 * <UnifiedLoadingSkeleton />
 * 
 * @example
 * // Auth loading context
 * <UnifiedLoadingSkeleton context="auth" message="Verifying session..." />
 * 
 * @example
 * // Explicit route override
 * <UnifiedLoadingSkeleton route="/dashboard" />
 */
export function UnifiedLoadingSkeleton({ 
  route, 
  context,
  message 
}: UnifiedLoadingSkeletonProps) {
  const detectedPath = useSafePathname();
  const pathname = route || detectedPath;

  // Handle special contexts first
  if (context === 'auth') {
    return <AuthLoadingSkeleton message={message || "Loading..."} />;
  }

  if (context === 'admin') {
    return <AdminSkeleton />;
  }

  // If no pathname available, fall back to auth skeleton
  if (!pathname) {
    return <AuthLoadingSkeleton message={message || "Loading..."} />;
  }

  // Route-based skeleton selection
  switch (pathname) {
    case '/dashboard':
      return <DashboardSkeleton />;
    
    case '/timer':
      return <TimerSkeleton />;
    
    case '/leaderboard':
      return <LeaderboardSkeleton />;
    
    case '/profile':
      return <ProfileSkeleton />;
    
    case '/shop':
      return <ShopSkeleton />;
    
    case '/groups':
      return <GroupsSkeleton />;
    
    case '/admin':
      return <AdminSkeleton />;
    
    case '/rest':
      return <BreakTimerSkeleton />;
    
    case '/rest/complete':
      return <BreakCompleteSkeleton />;
    
    case '/onboarding':
      return <OnboardingSkeleton />;
    
    // Auth-related pages get auth skeleton
    case '/auth':
    case '/forgot-password':
    case '/reset-password':
      return <AuthLoadingSkeleton message={message} />;
    
    // Fallback for unknown routes
    default:
      return <GenericSkeleton />;
  }
}
